import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bot, Copy, ExternalLink, MessageCircle, Pencil, Send, Sparkles } from 'lucide-react';
import api from '../../services/api';
import './d28d-admin-surfaces.css';

const HIDDEN_CHIPS = new Set(['pack_xn', 'semana']);

export default function EdithD28dAssistant({ onBack, onNavigate }) {
  const [displayName, setDisplayName] = useState('Asistente administrativo IA');
  const [chips, setChips] = useState([]);
  const [provider, setProvider] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('Asistente administrativo IA');
  const [question, setQuestion] = useState('');
  const [activeChip, setActiveChip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [flash, setFlash] = useState('');
  const [error, setError] = useState('');

  const flashOk = (msg) => {
    setError('');
    setFlash(msg);
    window.setTimeout(() => setFlash(''), 2500);
  };

  const loadMeta = useCallback(async () => {
    try {
      setError('');
      const r = await api.get('/d28d/edith/meta');
      const d = r.data?.data || {};
      setDisplayName(d.display_name || 'Asistente administrativo IA');
      setNameDraft(d.display_name || 'Asistente administrativo IA');
      setChips(Array.isArray(d.chips) ? d.chips.filter((c) => !HIDDEN_CHIPS.has(c.id)) : []);
      setProvider(d.provider || null);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo cargar Edith');
    }
  }, []);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  const saveName = async () => {
    const next = String(nameDraft || '').trim();
    if (!next) {
      setError('Nombre requerido');
      return;
    }
    try {
      const r = await api.patch('/d28d/edith/config', { display_name: next });
      setDisplayName(r.data?.data?.display_name || next);
      setEditingName(false);
      flashOk('Nombre actualizado');
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo guardar el nombre');
    }
  };

  const ask = async ({ chipId = activeChip, text = question } = {}) => {
    const q = String(text || '').trim();
    if (!q && !chipId) {
      setError('Escribe una pregunta o elige un chip');
      return;
    }
    setLoading(true);
    setError('');
    const userLine = q || chips.find((c) => c.id === chipId)?.label || chipId;
    setMessages((prev) => [...prev, { role: 'user', text: userLine }]);
    try {
      const r = await api.post('/d28d/edith/ask', {
        question: q || undefined,
        chip_id: chipId || undefined,
      });
      const d = r.data?.data || {};
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: d.reply || '',
          drafts: d.drafts || [],
          checklist: d.checklist || [],
          provider: d.provider,
          segment: d.segment || null,
        },
      ]);
      if (d.display_name) setDisplayName(d.display_name);
    } catch (e) {
      setError(e?.response?.data?.error || 'Edith no pudo responder');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
      setQuestion('');
    }
  };

  const visibleChips = useMemo(
    () => chips.filter((c) => !HIDDEN_CHIPS.has(c.id)),
    [chips],
  );

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      flashOk('Copiado');
    } catch {
      setError('No se pudo copiar');
    }
  };

  return (
    <div className="d28d-admin-surface ui-light-panel surface-readable space-y-4 p-4 max-w-3xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {editingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="input max-w-xs"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                aria-label="Nombre del asistente"
              />
              <button type="button" className="btn-primary text-sm" onClick={() => { void saveName(); }}>
                Guardar
              </button>
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => {
                  setEditingName(false);
                  setNameDraft(displayName);
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <h2 className="text-xl font-bold text-stone-900 m-0 flex items-center gap-2">
              <Bot className="w-5 h-5 text-stone-700" aria-hidden />
              {displayName}
              <button
                type="button"
                className="btn-secondary text-xs inline-flex items-center gap-1 py-1 px-2"
                onClick={() => setEditingName(true)}
                title="Editar nombre"
              >
                <Pencil size={12} /> Nombre
              </button>
            </h2>
          )}
          <p className="text-sm text-stone-600 mt-1 mb-0">
            Asistente administrativo · borradores WhatsApp · sin envío automático
          </p>
          {provider ? (
            <p className="text-xs text-stone-500 mt-1 mb-0">
              Motor: {provider.active_provider || '—'}
              {provider.note ? ` · ${provider.note}` : ''}
            </p>
          ) : null}
        </div>
        {onBack ? (
          <button type="button" className="btn-secondary text-sm" onClick={onBack}>
            Volver
          </button>
        ) : null}
      </div>

      {error ? <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 m-0">{error}</p> : null}
      {flash ? <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 m-0">{flash}</p> : null}

      <div className="flex flex-wrap gap-2">
        {visibleChips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activeChip === c.id
                ? 'bg-stone-900 text-white border-stone-900'
                : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500'
            }`}
            disabled={loading}
            onClick={() => {
              setActiveChip(c.id);
              void ask({ chipId: c.id, text: '' });
            }}
          >
            <Sparkles size={12} className="inline mr-1" aria-hidden />
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-3 min-h-[280px] max-h-[50vh] overflow-y-auto space-y-3">
        {messages.length === 0 ? (
          <p className="text-sm text-stone-500 m-0">
            Elige un chip o pregunta: «quiénes están sin entrenar», «no renovaron», «fotos premio»…
          </p>
        ) : null}
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`rounded-lg p-3 text-sm ${
              m.role === 'user' ? 'bg-stone-100 text-stone-900' : 'bg-emerald-50 text-stone-900 border border-emerald-100'
            }`}
          >
            <p className="font-semibold text-xs uppercase tracking-wide text-stone-500 mb-1">
              {m.role === 'user' ? 'Tú' : displayName}
              {m.provider ? ` · ${m.provider}` : ''}
            </p>
            <p className="m-0 whitespace-pre-wrap">{m.text}</p>
            {m.segment && Array.isArray(m.segment.users) ? (
              <div className="mt-3 bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="px-2 py-1.5 text-xs font-semibold text-stone-600 border-b border-stone-100 flex justify-between gap-2 items-center">
                  <span>{m.segment.label || 'Listado'} · {m.segment.total ?? m.segment.users.length}</span>
                  {m.segment.action_view && typeof onNavigate === 'function' ? (
                    <button
                      type="button"
                      className="btn-secondary text-xs inline-flex items-center gap-1 py-0.5 px-2 font-medium"
                      onClick={() => onNavigate(m.segment.action_view)}
                    >
                      <ExternalLink size={12} />
                      {m.segment.action_view === 'd28d-cycle-reports' ? 'Abrir reporte por ciclo' : 'Abrir Vigencias'}
                    </button>
                  ) : (
                    <span className="font-normal text-stone-400">Fit Live · Programa</span>
                  )}
                </div>
                {m.segment.truncated ? (
                  <p className="text-xs text-amber-800 bg-amber-50 m-0 px-2 py-1 border-b border-amber-100">
                    Mostrando {m.segment.users.length} de {m.segment.total}.
                  </p>
                ) : null}
                {m.segment.users.length === 0 ? (
                  <p className="text-xs text-stone-500 m-0 px-2 py-2">Sin usuarios en este estado.</p>
                ) : (
                  <ul className="m-0 p-0 list-none max-h-48 overflow-y-auto divide-y divide-stone-100">
                    {m.segment.users.map((u) => (
                      <li key={`${u.account_id || 'u'}-${u.user_id}`} className="px-2 py-1.5 text-xs text-stone-800 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">{u.nombre}</div>
                          <div className="text-stone-500">
                            {[u.email, u.plan, u.estado, u.detail].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {u.wa_url ? (
                          <a
                            href={u.wa_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary text-xs inline-flex items-center gap-1 py-0.5 px-2 shrink-0"
                          >
                            <MessageCircle size={12} /> WhatsApp
                          </a>
                        ) : (
                          <span className="text-stone-400 shrink-0">Sin teléfono</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
            {Array.isArray(m.checklist) && m.checklist.length > 0 ? (
              <ul className="mt-2 mb-0 pl-4 text-xs text-stone-600 space-y-0.5">
                {m.checklist.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            ) : null}
            {Array.isArray(m.drafts) && m.drafts.length > 0 ? (
              <div className="mt-3 space-y-2">
                {m.drafts.map((d, i) => (
                  <div key={i} className="bg-white border border-stone-200 rounded-lg p-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-semibold text-stone-500">
                        Borrador {d.channel || 'whatsapp'}
                      </span>
                      <button
                        type="button"
                        className="btn-secondary text-xs inline-flex items-center gap-1 py-0.5 px-2"
                        onClick={() => { void copyText(d.text); }}
                      >
                        <Copy size={12} /> Copiar
                      </button>
                    </div>
                    <p className="text-sm m-0 whitespace-pre-wrap">{d.text}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void ask({ chipId: activeChip, text: question });
        }}
      >
        <input
          className="input flex-1"
          placeholder={`Pregunta a ${displayName}…`}
          value={question}
          disabled={loading}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <button type="submit" className="btn-primary text-sm inline-flex items-center justify-center gap-1" disabled={loading}>
          <Send size={14} />
          {loading ? 'Pensando…' : 'Enviar'}
        </button>
      </form>
    </div>
  );
}
